import { useCallback, useEffect, useMemo, useState } from 'react';

import { useInfiniteQuery, useQuery } from 'react-query';
import {
  findOperationsByCNPJs,
  findOperationsSearch,
} from 'src/services/operation';
import { Operation } from 'src/types/operation';
import { WalletFund } from 'src/types/wallet';
import { formatCNPJ } from 'src/utils/document';
import { debounce } from 'src/utils/general';

import { useMonitorLimits } from './useMonitorLimits';
import {
  DuplicateGroup,
  DuplicateRecord,
} from '../CreateWallet/DuplicateRecordsModal';
import {
  createInitialQuota,
  EditableQuota,
  EditableWalletFund,
  flattenWalletFunds,
  removeEditableWalletFund,
} from '../Wallet/components/ficFundGroups';
import { CSVValidationResult } from '../validation';

const PAGE_SIZE = 100;

type QuotaRecord = EditableQuota;

type CsvQuota = {
  class: string;
  series: string;
  amount: number;
};

export type OperationWithRecords = EditableWalletFund;

export function hasWalletFundIdentity(
  fund: Pick<OperationWithRecords, 'id' | 'parentWalletFundId'>,
  operationId: number,
  parentWalletFundId?: number,
) {
  return (
    fund.id === operationId && fund.parentWalletFundId === parentWalletFundId
  );
}

export function findWalletFundByIdentity(
  funds: OperationWithRecords[],
  operationId: number,
  parentWalletFundId?: number,
) {
  return funds.find((fund) =>
    hasWalletFundIdentity(fund, operationId, parentWalletFundId),
  );
}

/** Root funds only — FIC children do not consume per-wallet fund slots. */
export function getRootFundsForLimitCount<
  T extends { parentWalletFundId?: number | null },
>(funds: T[]): T[] {
  return funds.filter((fund) => fund.parentWalletFundId == null);
}

type UseFundsManagementProps = {
  existingFunds?: WalletFund[];
  onSuccess?: () => void;
  onFundsChange?: (funds: OperationWithRecords[]) => void;
  onClearQuotaErrors?: () => void;
  onShowUpgradeModal?: (attemptedCount: number) => void;
  isInvitedUser?: boolean;
};

type ValidationResult = {
  newFunds: {
    operation: Operation;
    quotas: { class: string; series: string; amount: number }[];
  }[];
  existingFundUpdates: {
    walletFundId: number;
    operation: Operation;
    parentWalletFundId?: number;
    newQuotas: { class: string; series: string; amount: number }[];
    updatedQuotas: {
      existingIndex: number;
      class: string;
      series: string;
      amount: number;
    }[];
  }[];
  duplicates: {
    cnpj: string;
    class: string;
    series: string;
    entries: { amount: number; lineNumber: number }[];
  }[];
  notFoundFunds: { cnpj: string; class: string; series: string }[];
};

function matchesEditableFundId(fund: OperationWithRecords, id: string) {
  return (
    fund.walletFundId?.toString() === id ||
    fund.editorId === id ||
    fund.hashId === id ||
    fund.id.toString() === id
  );
}

function validateCsvDataWithExistingFunds(
  csvData: any[],
  availableOperations: Operation[],
  existingWalletFunds: WalletFund[],
  currentFunds: OperationWithRecords[],
  keepDuplicates = false,
): ValidationResult {
  const result: ValidationResult = {
    newFunds: [],
    existingFundUpdates: [],
    duplicates: [],
    notFoundFunds: [],
  };

  const groupedByOperation = new Map<
    number,
    { operation: Operation; quotas: CsvQuota[] }
  >();

  csvData.forEach((row: any) => {
    const cnpj = formatCNPJ(row.cnpj_fundo);
    const operation = availableOperations.find(
      (op) => formatCNPJ(op.cnpj) === cnpj,
    );

    const quota = {
      class: row.subclasse || '',
      series: row.serie || '',
      amount: Number(row.quantidade) || 0,
    };

    if (!operation) {
      result.notFoundFunds.push({
        cnpj,
        class: quota.class,
        series: quota.series,
      });
      return;
    }

    const groupedOperation = groupedByOperation.get(operation.id);
    if (groupedOperation) {
      groupedOperation.quotas.push(quota);
      return;
    }

    groupedByOperation.set(operation.id, { operation, quotas: [quota] });
  });

  groupedByOperation.forEach(({ operation, quotas }) => {
    const existingFund = findWalletFundByIdentity(currentFunds, operation.id);

    if (existingFund) {
      const newQuotas: { class: string; series: string; amount: number }[] = [];
      const updatedQuotas: {
        existingIndex: number;
        class: string;
        series: string;
        amount: number;
      }[] = [];

      if (keepDuplicates) {
        quotas.forEach((quota: any) => {
          newQuotas.push({
            class: quota.class,
            series: quota.series,
            amount: quota.amount,
          });
        });
      } else {
        const uniqueQuotas = new Map<string, any>();
        quotas.forEach((quota: any) => {
          const key = `${quota.class}-${quota.series}`;
          uniqueQuotas.set(key, quota);
        });

        Array.from(uniqueQuotas.values()).forEach((quota: any) => {
          const existingQuotaIndex = existingFund.quotas.findIndex(
            (q) => q.class === quota.class && q.series === quota.series,
          );

          if (existingQuotaIndex >= 0) {
            updatedQuotas.push({
              existingIndex: existingQuotaIndex,
              class: quota.class,
              series: quota.series,
              amount: quota.amount,
            });
          } else {
            newQuotas.push({
              class: quota.class,
              series: quota.series,
              amount: quota.amount,
            });
          }
        });
      }

      if (newQuotas.length > 0 || updatedQuotas.length > 0) {
        result.existingFundUpdates.push({
          walletFundId: existingFund.id,
          operation,
          parentWalletFundId: existingFund.parentWalletFundId,
          newQuotas,
          updatedQuotas,
        });
      }
      return;
    }

    if (keepDuplicates) {
      result.newFunds.push({
        operation,
        quotas: quotas.map((q: any) => ({
          class: q.class,
          series: q.series,
          amount: q.amount,
        })),
      });
    } else {
      const uniqueQuotas = new Map<string, any>();
      quotas.forEach((quota: any) => {
        const key = `${quota.class}-${quota.series}`;
        uniqueQuotas.set(key, quota);
      });

      result.newFunds.push({
        operation,
        quotas: Array.from(uniqueQuotas.values()).map((q: any) => ({
          class: q.class,
          series: q.series,
          amount: q.amount,
        })),
      });
    }
  });

  return result;
}

export function useFundsManagement({
  existingFunds = [],
  onSuccess,
  onFundsChange,
  onClearQuotaErrors,
  onShowUpgradeModal,
  isInvitedUser = false,
}: UseFundsManagementProps = {}) {
  const { limits, validateFundsAddition, getUniqueFundsCount } =
    useMonitorLimits();

  const [allFunds, setAllFunds] = useState<OperationWithRecords[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const [inputFocused, setInputFocused] = useState(false);

  const [document, setDocument] = useState<File | null>(null);
  const [csvValidation, setCsvValidation] =
    useState<CSVValidationResult | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [fundsFromCsv, setFundsFromCsv] = useState<any[]>([]);
  const [fundValidation, setFundValidation] = useState<ValidationResult | null>(
    null,
  );
  const [showValidationSummary, setShowValidationSummary] = useState(false);

  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateGroups, setDuplicateGroups] = useState<DuplicateGroup[]>([]);
  const [pendingDedup, setPendingDedup] = useState<any[] | null>(null);

  const [duplicateAlert, setDuplicateAlert] = useState({
    open: false,
    message: '',
  });

  const [keepAllDuplicatesFlag, setKeepAllDuplicatesFlag] = useState(false);

  const memoizedDuplicateAlert = useMemo(
    () => duplicateAlert,
    [duplicateAlert.open, duplicateAlert.message],
  );

  const debouncedSetSearch = useCallback(
    debounce((value: string) => {
      setSearchValue(value);
    }, 500),
    [],
  );

  const {
    data: operationsPages,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isLoadingOperations,
  } = useInfiniteQuery(
    ['operations-search', searchValue],
    ({ pageParam = 1 }) =>
      findOperationsSearch(searchValue, pageParam, PAGE_SIZE),
    {
      getNextPageParam: (lastPage) => lastPage.nextPage,
      enabled: true,
      retry: false,
      useErrorBoundary: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      keepPreviousData: true,
      staleTime: 1000 * 60 * 5,
    },
  );

  const { data: csvApiResponse, isLoading: isLoadingCsvOperations } = useQuery({
    queryKey: ['csv-operations', fundsFromCsv],
    queryFn: () =>
      findOperationsByCNPJs(
        fundsFromCsv?.map((f) => f.cnpj_fundo),
        ['FIDC'],
      ),
    enabled: fundsFromCsv.length > 0,
  });

  const operationsFromCsv = csvApiResponse?.found;
  const csvNotFoundCnpjs = csvApiResponse?.notFound || [];
  const csvNonConformingCnpjs = csvApiResponse?.nonConforming || [];

  const operations = useMemo(
    () =>
      operationsPages ? operationsPages.pages.flatMap((page) => page.data) : [],
    [operationsPages],
  );

  useEffect(() => {
    if (existingFunds && existingFunds.length > 0) {
      setAllFunds(flattenWalletFunds(existingFunds));
    }
  }, [existingFunds]);

  useEffect(() => {
    if (csvValidation?.isValid && csvValidation.data) {
      const csvData = csvValidation.data;

      const map = new Map<string, DuplicateRecord[]>();
      csvData.forEach((row: any, idx: number) => {
        const key = `${row.cnpj_fundo}||${row.subclasse || ''}||${row.serie || ''}`;
        if (!map.has(key)) map.set(key, []);
        const records = map.get(key);
        if (records) {
          records.push({
            cnpj_fundo: row.cnpj_fundo,
            subclasse: row.subclasse || '',
            serie: row.serie || '',
            quantidade: row.quantidade || '',
            index: idx,
          });
        }
      });

      const dups: DuplicateGroup[] = Array.from(map.entries())
        .filter(([_, records]) => records.length > 1)
        .map(([key, records]) => ({ key, records }));

      if (dups.length > 0) {
        setDuplicateGroups(dups);
        setShowDuplicateModal(true);
        setPendingDedup(csvData);
      } else {
        setFundsFromCsv(csvData);
        setPendingDedup(null);
      }
    }
  }, [csvValidation]);

  useEffect(() => {
    if (pendingDedup) return;

    if (operationsFromCsv && fundsFromCsv.length > 0) {
      setAllFunds((currentFunds) => {
        const validation = validateCsvDataWithExistingFunds(
          fundsFromCsv,
          operationsFromCsv,
          existingFunds,
          currentFunds,
          keepAllDuplicatesFlag,
        );

        setFundValidation(validation);
        setShowValidationSummary(true);

        return currentFunds;
      });
    }
  }, [
    operationsFromCsv,
    fundsFromCsv,
    existingFunds,
    pendingDedup,
    keepAllDuplicatesFlag,
  ]);

  const handleFundSelect = useCallback(
    (fund: Operation, parentWalletFundId?: number) => {
      const existsInFunds = allFunds.some((existingFund) =>
        hasWalletFundIdentity(existingFund, fund.id, parentWalletFundId),
      );

      if (existsInFunds) {
        setDuplicateAlert({
          open: true,
          message:
            parentWalletFundId === undefined
              ? `O fundo "${fund.name}" já está adicionado como fundo raiz nesta carteira.`
              : `O fundo "${fund.name}" já está adicionado a este FIC na carteira.`,
        });
        return;
      }

      // Only root funds consume the per-wallet fund limit.
      if (
        parentWalletFundId === undefined &&
        limits.hasLimits &&
        !isInvitedUser
      ) {
        const currentUniqueFunds = getUniqueFundsCount(
          getRootFundsForLimitCount(allFunds),
        );
        const validation = validateFundsAddition(
          currentUniqueFunds,
          1,
          isInvitedUser,
        );

        if (!validation.isValid) {
          onShowUpgradeModal?.(1);
          return;
        }
      }

      const editorId = `new-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2)}`;
      const newFund: OperationWithRecords = {
        ...fund,
        editorId,
        parentWalletFundId,
        compositionRole:
          parentWalletFundId === undefined ? 'root' : 'fic_child',
        useAlertDefault: true,
        quotas: [createInitialQuota(`quota-${editorId}-0`, parentWalletFundId)],
      };
      setAllFunds((prev) => [...prev, newFund]);
    },
    [
      allFunds,
      limits.hasLimits,
      getUniqueFundsCount,
      validateFundsAddition,
      onShowUpgradeModal,
      isInvitedUser,
    ],
  );

  const handleAddQuota = useCallback(
    (fundId: string) => {
      const realFundId = fundId.replace('fund-', '');

      setAllFunds((prev) => {
        const updatedFunds = prev.map((f) => {
          if (matchesEditableFundId(f, realFundId)) {
            const fundIdentifier = f.walletFundId ?? f.editorId ?? f.id;

            return {
              ...f,
              quotas: [
                ...f.quotas,
                {
                  id: `quota-${fundIdentifier}-${Date.now()}-${f.quotas.length}`,
                  class: '',
                  series: '',
                  amount: 0,
                },
              ],
            };
          }
          return f;
        });

        setTimeout(() => {
          onFundsChange?.(updatedFunds);
        }, 0);

        return updatedFunds;
      });
    },
    [onFundsChange],
  );

  const handleRemoveQuota = useCallback(
    (fundId: string, quotaId: string) => {
      const realFundId = fundId.replace('fund-', '');

      setAllFunds((prev) => {
        const updatedFunds = prev.map((f) => {
          if (matchesEditableFundId(f, realFundId)) {
            return {
              ...f,
              quotas: f.quotas.filter((q) => q.id !== quotaId),
            };
          }
          return f;
        });

        setTimeout(() => {
          onFundsChange?.(updatedFunds);
        }, 0);

        return updatedFunds;
      });
    },
    [onFundsChange],
  );

  const debouncedOnFundsChange = useCallback(
    debounce((funds: OperationWithRecords[]) => {
      onFundsChange?.(funds);
    }, 300),
    [onFundsChange],
  );

  const handleUpdateQuota = useCallback(
    (
      fundId: string,
      quotaId: string,
      field: keyof QuotaRecord,
      value: string | number,
    ) => {
      const realFundId = fundId.replace('fund-', '');

      setAllFunds((prev) => {
        const updatedFunds = prev.map((f) => {
          if (matchesEditableFundId(f, realFundId)) {
            return {
              ...f,
              quotas: f.quotas.map((q) =>
                q.id === quotaId ? { ...q, [field]: value } : q,
              ),
            };
          }
          return f;
        });

        debouncedOnFundsChange(updatedFunds);

        onClearQuotaErrors?.();

        return updatedFunds;
      });
    },
    [debouncedOnFundsChange, onClearQuotaErrors],
  );

  const handleRemoveFund = useCallback((fundId: string) => {
    setAllFunds((prev) => removeEditableWalletFund(prev, fundId));
  }, []);

  const handleSearchChange = useCallback(
    (value: string) => {
      debouncedSetSearch(value);
    },
    [debouncedSetSearch],
  );

  const handleInputFocus = useCallback((focused: boolean) => {
    setInputFocused(focused);
  }, []);

  const handleConfirmDuplicates = useCallback(
    (selected: { [key: string]: number }) => {
      if (!pendingDedup) return;

      const toRemove = new Set<number>();
      duplicateGroups.forEach((group) => {
        group.records.forEach((rec, idx) => {
          if (idx !== selected[group.key]) {
            toRemove.add(rec.index);
          }
        });
      });

      const filtered = pendingDedup.filter((_, idx) => !toRemove.has(idx));

      setPendingDedup(null);
      setShowDuplicateModal(false);
      setDuplicateGroups([]);
      setKeepAllDuplicatesFlag(false);

      setFundsFromCsv(filtered);
    },
    [pendingDedup, duplicateGroups],
  );

  const handleCancelDuplicates = useCallback(() => {
    setShowDuplicateModal(false);
    setDuplicateGroups([]);
    setPendingDedup(null);
    setFundsFromCsv([]);
    setKeepAllDuplicatesFlag(false);
  }, []);

  const handleKeepAllDuplicates = useCallback(() => {
    if (!pendingDedup) return;

    setKeepAllDuplicatesFlag(true);

    setPendingDedup(null);
    setShowDuplicateModal(false);
    setDuplicateGroups([]);

    setFundsFromCsv(pendingDedup);
  }, [pendingDedup]);

  const handleConfirmImport = useCallback(() => {
    if (!fundValidation) return;

    const newUniqueFunds = fundValidation.newFunds.reduce((acc, fund) => {
      const cnpj = formatCNPJ(fund.operation.cnpj);
      acc.add(cnpj);
      return acc;
    }, new Set<string>());

    if (limits.hasLimits && !isInvitedUser) {
      const currentUniqueFunds = getUniqueFundsCount(
        getRootFundsForLimitCount(allFunds),
      );
      const validation = validateFundsAddition(
        currentUniqueFunds,
        newUniqueFunds.size,
        isInvitedUser,
      );

      if (!validation.isValid) {
        onShowUpgradeModal?.(newUniqueFunds.size);
        handleCancelImport();
        return;
      }
    }

    setAllFunds((prev) => {
      const newFunds: OperationWithRecords[] = fundValidation.newFunds.map(
        (fund) => ({
          ...fund.operation,
          compositionRole: 'root',
          useAlertDefault: true,
          quotas: fund.quotas.map((quota, index) => ({
            id: `import-quota-${fund.operation.id}-${index}-${Date.now()}`,
            class: quota.class,
            series: quota.series,
            amount: quota.amount,
          })),
        }),
      );

      const updatedFunds = [...prev, ...newFunds];

      fundValidation.existingFundUpdates.forEach((update) => {
        const fundIndex = updatedFunds.findIndex((fund) =>
          hasWalletFundIdentity(
            fund,
            update.operation.id,
            update.parentWalletFundId,
          ),
        );

        if (fundIndex >= 0) {
          const existingFund = updatedFunds[fundIndex];
          const updatedQuotas = [...existingFund.quotas];

          update.newQuotas.forEach((newQuota, index) => {
            updatedQuotas.push({
              id: `update-quota-${update.operation.id}-${updatedQuotas.length + index}-${Date.now()}`,
              class: newQuota.class,
              series: newQuota.series,
              amount: newQuota.amount,
            });
          });

          update.updatedQuotas.forEach((updatedQuota) => {
            if (updatedQuota.existingIndex < updatedQuotas.length) {
              updatedQuotas[updatedQuota.existingIndex] = {
                ...updatedQuotas[updatedQuota.existingIndex],
                class: updatedQuota.class,
                series: updatedQuota.series,
                amount: updatedQuota.amount,
              };
            }
          });

          updatedFunds[fundIndex] = {
            ...existingFund,
            quotas: updatedQuotas,
          };
        }
      });

      setTimeout(() => {
        onFundsChange?.(updatedFunds);
      }, 0);

      return updatedFunds;
    });

    setShowValidationSummary(false);
    setFundValidation(null);
    setDocument(null);
    setCsvValidation(null);
    setFundsFromCsv([]);
    setKeepAllDuplicatesFlag(false);
  }, [
    fundValidation,
    onFundsChange,
    limits.hasLimits,
    allFunds,
    getUniqueFundsCount,
    validateFundsAddition,
    onShowUpgradeModal,
    isInvitedUser,
  ]);

  const handleCancelImport = useCallback(() => {
    setShowValidationSummary(false);
    setFundValidation(null);
    setDocument(null);
    setCsvValidation(null);
    setFundsFromCsv([]);
    setKeepAllDuplicatesFlag(false);
  }, []);

  const handleCloseDuplicateAlert = () => {
    setDuplicateAlert({ open: false, message: '' });
  };

  return useMemo(
    () => ({
      allFunds,
      searchValue,
      duplicateAlert: memoizedDuplicateAlert,
      showDuplicateModal,
      duplicateGroups,
      document,
      csvValidation,
      isValidating,
      showValidationSummary,
      fundValidation,
      operations,
      isLoadingOperations,
      isLoadingCsvOperations,
      hasNextPage,
      isFetchingNextPage,
      csvNotFoundCnpjs,
      csvNonConformingCnpjs,
      handleFundSelect,
      handleAddQuota,
      handleRemoveQuota,
      handleUpdateQuota,
      handleRemoveFund,
      handleSearchChange,
      handleConfirmDuplicates,
      handleCancelDuplicates,
      handleKeepAllDuplicates,
      handleConfirmImport,
      handleCancelImport,
      handleCloseDuplicateAlert,
      fetchNextPage,
      setDocument,
      setCsvValidation,
      setIsValidating,
    }),
    [
      allFunds,
      searchValue,
      memoizedDuplicateAlert,
      showDuplicateModal,
      duplicateGroups,
      document,
      csvValidation,
      isValidating,
      showValidationSummary,
      fundValidation,
      operations,
      isLoadingOperations,
      isLoadingCsvOperations,
      hasNextPage,
      isFetchingNextPage,
      csvNotFoundCnpjs,
      csvNonConformingCnpjs,
      handleFundSelect,
      handleAddQuota,
      handleRemoveQuota,
      handleUpdateQuota,
      handleRemoveFund,
      handleSearchChange,
      handleConfirmDuplicates,
      handleCancelDuplicates,
      handleKeepAllDuplicates,
      handleConfirmImport,
      handleCancelImport,
      handleCloseDuplicateAlert,
      fetchNextPage,
      setDocument,
      setCsvValidation,
      setIsValidating,
    ],
  );
}
